// Auth endpoints live at /auth/* (outside /api/*) so this client uses raw
// fetch instead of the /api-prefixed helper.

export type AgencyLoginStatus = 'onboarding' | 'active' | 'suspended';

export interface AgencyLookup {
  id: string;
  display_name: string;
  status: AgencyLoginStatus;
  has_m365: boolean;
}

export interface CurrentUser {
  user_id: string;
  email: string;
  name: string | null;
  agency_id: string;
  permissions: {
    role: 'superadmin' | 'admin' | 'technician' | 'readonly';
    tenant_scope: string[] | '*';
    can_execute_standards: boolean;
    can_manage_webhooks: boolean;
    can_use_ai: boolean;
  };
}

interface AuthApiResponse<T> {
  success: boolean;
  data?: T;
  error?: { code: string; message: string };
}

async function authFetch<T>(path: string, options?: RequestInit): Promise<T> {
  const res = await fetch(path, { credentials: 'include', ...options });
  if (res.status === 401) throw new Error('Not authenticated');
  const json = (await res.json()) as AuthApiResponse<T>;
  if (!json.success || json.data === undefined) {
    throw new Error(json.error?.message ?? `Request failed: ${res.status}`);
  }
  return json.data;
}

export const authApi = {
  me: () => authFetch<CurrentUser>('/auth/me'),
  lookup: (slug: string) => authFetch<AgencyLookup>(`/auth/lookup/${encodeURIComponent(slug)}`),
  logout: () => fetch('/auth/logout', { method: 'POST', credentials: 'include' }),
};
